import React from 'react';

import {SkeletonBlock} from 'src/components/skeleton-block/skeleton-block';
import {PB_ADMIN} from 'src/hooks/access-resource-constant';
import {useCurrentUser} from 'src/hooks/useCurrentUser';

import {AvatarWrapped} from '@pb/kit-component-avatar';
import Button from '@pb/kit-component-button';
import EllipsisTextHint from '@pb/kit-component-ellipsis-text-hint';
import Group from '@pb/kit-component-group';
import {Icons} from '@pb/kit-component-icon';
import {Tooltip} from '@pb/kit-component-popups';
import Typography from '@pb/kit-component-typography';


import {
    actions,
    header,
    titleRow,
} from '../vendor-profile.module.styl';


type Props = {
    vendorDisplayName?: string,
    isLoadingName?: boolean,
    canSave: boolean,
    onSave: () => void,
    onDelete?: () => void,
    deleteDisabled?: boolean,
    deleteTooltip?: string,
};

export const VendorProfileHeader: React.FC<Props> = ({
    vendorDisplayName,
    isLoadingName = false,
    canSave,
    onSave,
    onDelete,
    deleteDisabled = true,
    deleteTooltip,
}) => {
    const {hasAccessToResource} = useCurrentUser();
    const canDeleteVendor = hasAccessToResource(PB_ADMIN.DELETE_VENDOR);
    const canUpdateVendor = hasAccessToResource(PB_ADMIN.UPDATE_VENDOR);

    const deleteButton = (
        <Button
            variant="Type.SECONDARY"
            disabled={deleteDisabled}
            colorVariant="ERROR"
            onClick={onDelete}>
            Delete
        </Button>
    );

    const renderDeleteButton = () => {
        if (deleteTooltip && deleteDisabled) {
            return (
                <Tooltip
                    placement="bottom-start"
                    color="light"
                    anchor={({anchorRef, getAnchorProps}) => (
                        <span ref={anchorRef} {...getAnchorProps()} style={{display: 'inline-flex'}}>
                            {deleteButton}
                        </span>
                    )}>
                    {deleteTooltip}
                </Tooltip>
            );
        }
        return deleteButton;
    };

    return (
        <div className={header}>
            <div className={titleRow}>
                <Group gap="m" alignItems="center">
                    <AvatarWrapped
                        src=""
                        alt={vendorDisplayName || 'Vendor'}
                        placeholder={Icons.COMPANIES_FILLED}
                    />
                    {isLoadingName ? (
                        <SkeletonBlock width={220} height={32}/>
                    ) : (
                        <Typography style={{maxWidth: 350}} variant="heading-level-1">
                            <EllipsisTextHint title={vendorDisplayName}>
                                {vendorDisplayName}
                            </EllipsisTextHint>
                        </Typography>
                    )}
                </Group>
            </div>
            <div className={actions}>
                {canDeleteVendor ? (
                    renderDeleteButton()
                ) : null}
                {canUpdateVendor ? (
                    <Button
                        variant="Type.PRIMARY"
                        disabled={!canSave}
                        onClick={onSave}>
                        Save
                    </Button>
                ) : null}
            </div>
        </div>
    );
};
